"use client";
import clsx from "clsx";
import Link from "next/link";
import { ComponentProps, JSX, ReactNode, memo, useCallback, useState } from "react";

import Loading from "components/Loading";

export const ButtonStyle = {
  PRIMARY: "primary",
  WHITE: "white",
  OUTLINE: "outline",
  RED_OUTLINE: "redOutline",
  SHADOW: "shadow",
  PRIMARY_OUTLINE: "primaryOutline",
} as const;

const switchColorByStyle = (
  style:
    | typeof ButtonStyle.PRIMARY
    | typeof ButtonStyle.WHITE
    | typeof ButtonStyle.OUTLINE
    | typeof ButtonStyle.RED_OUTLINE
    | typeof ButtonStyle.SHADOW
    | typeof ButtonStyle.PRIMARY_OUTLINE
): "green" | "red" | "white" => {
  switch (style) {
    case "redOutline":
      return "red";
    case "outline":
      return "white";
    default:
      return "green";
  }
};

export type ButtonProps = {
  text: ReactNode;
  isDisabled?: boolean;
  replace?: boolean;
  fontSize?: string;
  preElement?: JSX.Element;
  style?: (typeof ButtonStyle)[keyof typeof ButtonStyle];
  onClick?: () => Promise<void> | void;
  width?: string;
  minWidth?: string;
  height?: string;
  href?: string;
  isLoading?: boolean;
} & Omit<ComponentProps<"button">, "style">;

/**
 * ボタンは2種類存在
 * 1.ユーザーのアクションを担当するbutton (onClick functionを使いたい場合)
 * 2.画面遷移を担当するLink (hrefを受け入れる 画面遷移をしたい場合)
 * hrefを引数に指定すると自動的にLinkになる
 * 何も指定しない場合はbuttonになる
 * */
export const Button = memo(
  ({
    text = "ボタン",
    type = "button",
    isDisabled,
    replace = false,
    fontSize = "text-base",
    preElement,
    style = ButtonStyle.PRIMARY,
    onClick,
    width = "w-full",
    minWidth = "min-w-[160px]",
    height = "h-12",
    href,
    isLoading = false,
    ...props
  }: ButtonProps) => {
    const styles = {
      [ButtonStyle.PRIMARY]: "bg-link hover:bg-link-dark text-white font-bold",
      [ButtonStyle.PRIMARY_OUTLINE]: "bg-white-f2 text-link",
      [ButtonStyle.WHITE]: "bg-white hover:bg-gray hover:text-white text-gray border border-gray",
      [ButtonStyle.OUTLINE]: "bg-white hover:bg-link-light text-link border border-link",
      [ButtonStyle.RED_OUTLINE]: "bg-white hover:bg-warning hover:text-white text-warning border border-warning",
      [ButtonStyle.SHADOW]: "bg-white hover:bg-primary hover:text-white text-gray border-gray shadow-common",
    };

    const [isBusy, setIsBusy] = useState(false);
    const onClickHandler = useCallback(async () => {
      if (!isDisabled && !isBusy) {
        setIsBusy(true);
        try {
          await onClick?.();
        } catch (error) {
          setIsBusy(false);
        }
        setIsBusy(false);
      }
    }, [isDisabled, onClick, isBusy]);

    if (href) {
      if (isDisabled) {
        return (
          <div
            className={clsx(
              `flex ${height} ${fontSize} ${width} cursor-not-allowed items-center justify-center rounded text-16px transition duration-300 ease-in-out`,
              "bg-button-disabled text-white"
            )}
          >
            {text}
          </div>
        );
      }

      return (
        <Link
          href={isDisabled ? "" : href}
          replace={replace}
          className={clsx(
            `flex ${height} ${fontSize} ${width} items-center justify-center rounded text-16px transition duration-300 ease-in-out`,
            styles[style]
          )}
        >
          {text}
        </Link>
      );
    }

    return (
      <button
        {...props}
        type={type}
        className={clsx(
          `flex ${height} ${minWidth} ${fontSize} relative items-center justify-center rounded text-16px transition duration-300 ease-in-out`,
          isBusy || isDisabled || isLoading ? "bg-button-disabled text-white" : styles[style],
          width
        )}
        onClick={onClickHandler}
        disabled={isDisabled || isLoading || isBusy}
      >
        {(isBusy || isLoading) && (
          <div className="absolute left-2 flex items-center justify-center">
            <Loading height="14px" color={switchColorByStyle(style)} />
          </div>
        )}
        {preElement != null && !isDisabled && <div className="mr-2 flex items-center justify-center">{preElement}</div>}
        {isBusy || isLoading ? <div className="ml-4">{text}</div> : text}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
